import { type FC } from "react";

import {
  formatterByMetricsKey,
  globalMetricsLineOrder,
  labelByMetricsKey,
} from "~/feature/globalMetrics/components/GlobalMetricsLine/constants";
import type { IGetGlobalMetricsResponseBody } from "~/feature/globalMetrics/types";
import { MetricEnum } from "~/types/metrics";
import { formatNumber } from "~/utils/formatValue";

import * as Styled from "./GlobalMetricsLine.styles";

interface IGlobalMetricsLineItemsProps {
  data: IGetGlobalMetricsResponseBody | null;
  groupId: string;
}

export const GlobalMetricsLineItems: FC<IGlobalMetricsLineItemsProps> = ({
  data,
  groupId,
}) => {
  return (
    <>
      {globalMetricsLineOrder.map((metricKey) => {
        if (
          metricKey === MetricEnum.TotalActiveEvents &&
          data?.[MetricEnum.TotalActiveEvents] &&
          data?.[MetricEnum.TotalMarkets]
        ) {
          return (
            <Styled.Item key={`${groupId}-${metricKey}`}>
              <Styled.Value>
                {formatNumber(data?.[MetricEnum.TotalMarkets], {
                  withDollar: false,
                })}
              </Styled.Value>
              <Styled.Label>Markets across</Styled.Label>
              <Styled.Value>
                {formatNumber(data?.[MetricEnum.TotalActiveEvents], {
                  withDollar: false,
                })}
              </Styled.Value>
              <Styled.Label>events</Styled.Label>
            </Styled.Item>
          );
        }

        if (
          metricKey === MetricEnum.Transactions24h &&
          data?.dune?.total?.transactions
        ) {
          return (
            <Styled.Item key={`${groupId}-${metricKey}`}>
              <Styled.Label>{labelByMetricsKey[metricKey]}:</Styled.Label>
              <Styled.Value>
                {formatNumber(data?.dune?.total?.transactions, {
                  withDollar: false,
                })}
              </Styled.Value>
            </Styled.Item>
          );
        }

        if (
          metricKey === MetricEnum.KalshiPolymarketVolumeStats &&
          data?.[MetricEnum.KalshiPolymarketVolumeStats]
        ) {
          return (
            <Styled.Platforms key={`${groupId}-${metricKey}`}>
              <Styled.Item>
                <Styled.Label>Polymarket:</Styled.Label>
                <Styled.Value>
                  {
                    data?.[MetricEnum.KalshiPolymarketVolumeStats]
                      ?.polymarketVolumeSharePercentOfKalshiPolymarketCombined
                  }
                  %
                </Styled.Value>
              </Styled.Item>
              <Styled.Label>VS.</Styled.Label>
              <Styled.Item>
                <Styled.Label>Kalshi:</Styled.Label>
                <Styled.Value>
                  {
                    data?.[MetricEnum.KalshiPolymarketVolumeStats]
                      ?.kalshiVolumeSharePercentOfKalshiPolymarketCombined
                  }
                  %
                </Styled.Value>
              </Styled.Item>
            </Styled.Platforms>
          );
        }

        if (
          metricKey === MetricEnum.PlatformEventShare &&
          data?.[MetricEnum.PlatformEventShare]
        ) {
          return (
            <Styled.Item key={`${groupId}-${metricKey}`}>
              <Styled.Label>{labelByMetricsKey[metricKey]}:</Styled.Label>
              <Styled.Value>
                {formatNumber(data?.[MetricEnum.PlatformEventShare].length, {
                  withDollar: false,
                })}
              </Styled.Value>
            </Styled.Item>
          );
        }

        // MetricEnum carries FE-logical keys (e.g. Transactions24h) that are
        // NOT top-level contract fields — those are special-cased above (e.g.
        // read from data.dune.total). Here only the flat numeric metrics remain.
        const metricValue =
          data?.[metricKey as keyof IGetGlobalMetricsResponseBody];
        const label = labelByMetricsKey[metricKey];

        if (!label || !metricValue || typeof metricValue === "object")
          return null;

        const format = formatterByMetricsKey[metricKey];
        const formatted = format?.(metricValue) || metricValue;

        return (
          <Styled.Item key={`${groupId}-${metricKey}`}>
            <Styled.Label>{label}:</Styled.Label>
            <Styled.Value>{formatted}</Styled.Value>
          </Styled.Item>
        );
      })}
    </>
  );
};
